import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {
    DartsPlayerPayload,
    DartsPlayerType,
    DartsPlayersTable, IUploadPhotoPayload, DeletePlayerPayload,
} from "./DartsPlayersCRUD.types";
import {toastr} from "react-redux-toastr";
import { API_PLAYERS, API_PLAYERS_PHOTO } from "../../../services/api/api.urls";
import { COMPANY_ABBR } from "../../../common/constants/company";
import { api } from "../../../services/api/api.service";
import { upload } from "../../../services/upload.api";
import { TryCatch } from "../../../services/TryCatch";

export interface IGetPlayersResponse {
    totalItems: number,
    totalPages: number,
    pageNum: number,
    pageSize: number,
    items: DartsPlayerType[]
}

const initialState: DartsPlayersTable = {
    dartsPlayers: [] as DartsPlayerType[],
    loadingPlayers: false,
    editMode: false,
    editPlayerId: 0,
    isVisibleForm: false,
    noContent: false,
    isEditPhotoMode: false,
    isShowModal: false,
    photoLoader: false,
    pagination: {
        totalPages: 1,
        activePage: 1,
        pageSize: 10,
    }
}

export const getDartsPlayers = createAsyncThunk<IGetPlayersResponse, string, { rejectValue: string }>(
    'dartsPlayers/get',
    async (query, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_PLAYERS}?${query}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to fetch players.");
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const invitePlayer = createAsyncThunk<void, { email: string }, { rejectValue: string }>(
    'dartsPlayers/invite-player',
    async (payload, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.post(`${API_PLAYERS}/invite`, payload).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to send invitation.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Invitation have sent.");
        return result.data;
    }
)

export const createDartsPlayer = createAsyncThunk<DartsPlayerType, DartsPlayerPayload, { rejectValue: string }>(
    'dartsPlayers/post',
    async (player, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.post(API_PLAYERS, player).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to create a player.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Darts player has been created")
        return result.data;
    }
)

export const editDartsPlayer = createAsyncThunk<DartsPlayerType, DartsPlayerPayload, { rejectValue: string }>(
    'dartsPlayers/edit',
    async (payload, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.put(`${API_PLAYERS}`, payload).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to edit a player.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Player has been edited");
        return result.data;
    }
)

export const deleteDartsPlayer = createAsyncThunk<DartsPlayerType, number, { rejectValue: string }>(
    'delete-darts-player',
    async (id, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.delete(`${API_PLAYERS}/${id}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to delete a player.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Player has been deleted");
        return result.data;
    }
)

export const uploadPlayersPhoto = createAsyncThunk<string, IUploadPhotoPayload, { rejectValue: string }>(
    'dartsPlayersPhoto/upload',
    async (payload, {rejectWithValue}) => {
        const result = await TryCatch(
            () => upload.post(`${API_PLAYERS_PHOTO}/${payload.id}`, payload.picture).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to upload a player's photo.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Player's photo has been uploaded.");
        return result.data;
    }
)

export const updatePlayersPhoto = createAsyncThunk<string, IUploadPhotoPayload, { rejectValue: string }>(
    'dartsPlayersPhoto/update',
    async (payload, {rejectWithValue}) => {
        const result = await TryCatch(
            () => upload.put(`${API_PLAYERS_PHOTO}/${payload.id}`, payload.picture).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to update a player's photo.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Player's photo has been updated.");
        return result.data;
    }
)

export const deletePlayersPhoto = createAsyncThunk<DartsPlayerType, DeletePlayerPayload, { rejectValue: string }>(
    'dartsPlayersPhoto/delete',
    async ({playerId, setIsOpenForm}, {rejectWithValue}) => {
        const result = await TryCatch(
            () => upload.delete(`${API_PLAYERS_PHOTO}/${playerId}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to delete a player's photo.");
            return rejectWithValue(result.error);
        }
        setIsOpenForm(false)
        toastr.success(COMPANY_ABBR, "Player's photo has been deleted");
        return result.data;
    }
)


export const DartsPlayersCRUD = createSlice({name: 'DartsPlayersCRUD',
    reducers: {
        setPageNumber: (state, action) => {
            state.pagination.activePage = action.payload
        },
        changePageSize: (state, action) => {
            state.pagination.pageSize = action.payload
        },
        setPlayersIsShowModal: (state, action: PayloadAction<boolean>) => {
            state.isShowModal = action.payload
        },
        setPlayersEditPhotoMode: (state, action: PayloadAction<boolean>) => {
            state.isEditPhotoMode = action.payload
        },
        playersShowMode: (state) => {
            state.isVisibleForm = true;
            state.editMode = false;
        },
        playersEditMode: (state, action: PayloadAction<number>) => {
            state.editMode = true;
            state.editPlayerId = action.payload;
            state.isVisibleForm = true;
        },
        playersEditPhotoMode: (state, action: PayloadAction<boolean>) => {
            state.isEditPhotoMode = action.payload;
        },
        playersHideForm: (state) => {
            state.isVisibleForm = false;
        },
        playerSetModal: (state, action: PayloadAction<boolean>) => {
            state.isShowModal = action.payload;
        },
        setPlayerPhotoLoader: (state, action: PayloadAction<boolean>) => {
            state.photoLoader = action.payload;
        },

    },
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(getDartsPlayers.pending, (state: DartsPlayersTable) => {
                state.loadingPlayers = true;
            })
            .addCase(getDartsPlayers.fulfilled, (state: DartsPlayersTable, action) => {
                state.dartsPlayers = action.payload.items;
                state.pagination.totalPages = action.payload.totalPages;
                state.noContent = !action.payload.items;
                state.loadingPlayers = false;
            })
            .addCase(getDartsPlayers.rejected, (state: DartsPlayersTable) => {
                state.noContent = true;
                state.loadingPlayers = false;
            })
            .addCase(deleteDartsPlayer.pending, (state: DartsPlayersTable) => {
                state.loadingPlayers = true;
            })
            .addCase(deleteDartsPlayer.fulfilled, (state: DartsPlayersTable, action) => {
                state.dartsPlayers = state.dartsPlayers.filter(player => player.id !== action.meta.arg);
                state.loadingPlayers = false;
            })
            .addCase(deleteDartsPlayer.rejected, (state: DartsPlayersTable) => {
                state.loadingPlayers = false;
            })
            .addCase(createDartsPlayer.pending, (state: DartsPlayersTable) => {
                state.loadingPlayers = true;
            })
            .addCase(createDartsPlayer.fulfilled, (state: DartsPlayersTable, action) => {
                state.dartsPlayers = state.dartsPlayers.concat(action.payload);
                state.isVisibleForm = false;
                state.loadingPlayers = false;
            })
            .addCase(createDartsPlayer.rejected, (state: DartsPlayersTable) => {
                state.loadingPlayers = false;
            })
            .addCase(editDartsPlayer.pending, (state: DartsPlayersTable) => {
                state.isVisibleForm = false;
                state.loadingPlayers = true;

            })
            .addCase(editDartsPlayer.fulfilled, (state: DartsPlayersTable, action) => {
                state.editPlayerId = action.meta.arg.id!;
                state.editMode = true;
                state.dartsPlayers = state.dartsPlayers.map(player => player.id === action.payload.id ? action.payload : player)
                state.loadingPlayers = false;
            })
            .addCase(editDartsPlayer.rejected, (state: DartsPlayersTable) => {
                state.loadingPlayers = false;
            })
            .addCase(updatePlayersPhoto.pending, (state: DartsPlayersTable) => {
                state.loadingPlayers = true;
            })
            .addCase(updatePlayersPhoto.fulfilled, (state: DartsPlayersTable, action) => {
                state.loadingPlayers = false;
                state.dartsPlayers.map(player => {
                    if (player.id === action.meta.arg.id) {
                        player.avatar = action.payload
                    }
                    return player
                })
            })
            .addCase(updatePlayersPhoto.rejected, (state: DartsPlayersTable) => {
                state.loadingPlayers = false;
            })
            .addCase(uploadPlayersPhoto.pending, (state: DartsPlayersTable) => {
                state.loadingPlayers = true;
            })
            .addCase(uploadPlayersPhoto.fulfilled, (state: DartsPlayersTable, action) => {
                state.loadingPlayers = false;
                state.dartsPlayers.map(player => {
                    if (player.id === action.meta.arg.id) {
                        player.avatar = action.payload
                    }
                    return player
                })
            })
            .addCase(uploadPlayersPhoto.rejected, (state: DartsPlayersTable) => {
                state.loadingPlayers = false;
            })
            .addCase(deletePlayersPhoto.pending, (state: DartsPlayersTable) => {
                state.loadingPlayers = true;
                state.isShowModal = false;
                state.photoLoader = true
            })
            .addCase(deletePlayersPhoto.fulfilled, (state: DartsPlayersTable, action) => {
                state.dartsPlayers.map(player => {
                    if (player.id === action.meta.arg.playerId) {
                        player.avatar = ""
                    }
                    return player
                })
                state.loadingPlayers = false;
                state.photoLoader = false
                state.isShowModal = false
            })
            .addCase(deletePlayersPhoto.rejected, (state: DartsPlayersTable) => {
                state.loadingPlayers = false;
                state.isShowModal = false
                state.photoLoader = false
            })
    },
});

export const {
    setPageNumber,
    setPlayersIsShowModal,
    setPlayersEditPhotoMode,
    playersShowMode,
    playersEditMode,
    playersEditPhotoMode,
    playersHideForm,
    changePageSize
} = DartsPlayersCRUD.actions
export default DartsPlayersCRUD.reducer;

